import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@repo/ui';
import { Loader2 } from 'lucide-react';
import { type ReactElement, useEffect, useId, useState } from 'react';
import { useProduct } from '@/features/catalog/api/products';
import { QuotationForm } from '@/features/sales/components/QuotationForm';
import type { CreateQuotationFormValues } from '@/features/sales/types/quotation.types';
import { useAuthStore } from '@/stores/auth.store';
import { useCalculatorCurrencies, useProductsSearch } from '../api/calculator';
import { RATE_TERM_MAX_MONTHS, RATE_TERM_MIN_MONTHS } from '../lib/calculator-math';
import { useCalculatorState } from '../hooks/useCalculatorState';
import type { CalculatorProduct } from '../types/calculator.types';

/**
 * CalculatorModal — standalone quote calculator (Slice 8 PR#1).
 *
 * 3-column layout on desktop (inputs / outputs / actions), stacked on
 * mobile. Inputs feed `useCalculatorState`, which derives outputs via
 * the decimal.js pricing engine (SSOT) and persists inputs to
 * localStorage. Role gates the cost-derived outputs: WAREHOUSE never
 * sees `margin`.
 *
 * Polish PR installment — the modal shows the resulting monthly
 * installment under the `total`. The interest rate is NOT surfaced to
 * the user; the calculator derives it from `tasaMensual(meses)` based
 * on the term the user types (a continuous linear curve between
 * RATE_TERM_MIN_MONTHS → 3.0% and RATE_TERM_MAX_MONTHS → 5.5%).
 * Margin stays role-gated; installment carries no cost and is shown
 * for every role.
 *
 * Polish PR term-input — the term is a free-form numeric input rather
 * than a fixed set of chips. The UI clamps to [1, 120] months for
 * usability; the underlying math accepts any positive integer.
 *
 * "Enviar a cotización" pre-fills `QuotationForm` via its
 * `initialValues` prop (currency, tax flag, and a single seeded item).
 * Esc / overlay click close the dialog (Radix default).
 */
export interface CalculatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** UX clamp so the user doesn't accidentally type 0 or 240 (20 years). */
const TERM_INPUT_MIN = 1;
const TERM_INPUT_MAX = 120;

export function CalculatorModal({ open, onOpenChange }: CalculatorModalProps): ReactElement {
  const uid = useId();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'SELLER';
  const tenantId = user?.tenantId ?? 'anonymous';
  const canSeeCosts = role !== 'WAREHOUSE';

  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<CalculatorProduct | null>(null);
  const [quotationOpen, setQuotationOpen] = useState(false);
  const [quotationInitial, setQuotationInitial] = useState<
    Partial<CreateQuotationFormValues> | undefined
  >(undefined);

  const productsQuery = useProductsSearch(search);
  const currenciesQuery = useCalculatorCurrencies();

  const { input, output, setInput, reset } = useCalculatorState({
    role,
    tenantId,
    product: selectedProduct,
  });

  // Resolve the selected product (works after localStorage restore too,
  // where we only have a productId). Syncing into state — declared above
  // the hook — feeds the resolved product back so outputs recompute.
  const productDetail = useProduct(input.productId || null);
  useEffect(() => {
    const p = productDetail.data;
    if (p) {
      setSelectedProduct({
        id: p.id,
        name: p.name,
        sku: p.sku,
        defaultSalePrice: p.defaultSalePrice,
        defaultCost: p.defaultCost,
      });
    } else if (!input.productId) {
      setSelectedProduct(null);
    }
  }, [productDetail.data, input.productId]);

  const displayOutput = output;

  const currencies = currenciesQuery.data?.data ?? [];
  const selectedCurrency = currencies.find((c) => c.id === input.currencyId) ?? null;
  const decimals = selectedCurrency?.decimalPlaces ?? 2;
  const symbol = selectedCurrency?.symbol ?? '$';

  const fmt = (value: number): string => `${symbol} ${value.toFixed(decimals)}`;

  const handleSendToQuotation = () => {
    if (!selectedProduct || !input.currencyId) return;
    const unitPrice = selectedProduct.defaultSalePrice;
    const quantity = input.quantity || '1';
    const subtotal = displayOutput.subtotal.toFixed(4);
    const values: Partial<CreateQuotationFormValues> = {
      currencyId: input.currencyId,
      includesTax: input.includeTax,
      items: [
        {
          productId: selectedProduct.id,
          quantity,
          unitPrice,
          subtotal,
          notes: null,
        },
      ],
    };
    setQuotationInitial(values);
    onOpenChange(false);
    setQuotationOpen(true);
  };

  const productResults = productsQuery.data ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Calculadora de cotización</DialogTitle>
            <DialogDescription>
              Simula precios, descuentos e impuestos sin guardar. Enviá el resultado a una
              cotización cuando estés listo.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 md:grid-cols-3">
            {/* ── Column 1: Inputs ───────────────────────────────── */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor={`${uid}-product-search`}>Producto</Label>
                <Input
                  id={`${uid}-product-search`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre o SKU…"
                  autoComplete="off"
                />
                {productsQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                    Buscando…
                  </div>
                ) : null}
                {productResults.length > 0 ? (
                  <ul className="max-h-40 overflow-auto rounded-md border">
                    {productResults.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => setInput({ productId: p.id })}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted ${
                            p.id === input.productId ? 'bg-muted font-medium' : ''
                          }`}
                        >
                          <span className="truncate">{p.name}</span>
                          <span className="ml-2 font-mono text-xs text-muted-foreground">
                            {p.sku}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {selectedProduct ? (
                  <p className="text-xs text-muted-foreground">
                    Seleccionado: {selectedProduct.name} · precio {selectedProduct.defaultSalePrice}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${uid}-quantity`}>Cantidad</Label>
                <Input
                  id={`${uid}-quantity`}
                  type="number"
                  step="0.0001"
                  min={0}
                  value={input.quantity}
                  onChange={(e) => setInput({ quantity: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${uid}-discount`}>Descuento (%)</Label>
                <Input
                  id={`${uid}-discount`}
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={input.discountPercent}
                  onChange={(e) => setInput({ discountPercent: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${uid}-currency`}>Moneda</Label>
                <Select
                  {...(input.currencyId ? { value: input.currencyId } : {})}
                  onValueChange={(value) => setInput({ currencyId: value })}
                >
                  <SelectTrigger id={`${uid}-currency`}>
                    <SelectValue placeholder="Elegí una moneda" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.code} ({c.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${uid}-rate`}>Tipo de cambio (a moneda base)</Label>
                <Input
                  id={`${uid}-rate`}
                  type="number"
                  step="0.0001"
                  min={0}
                  value={input.exchangeRate}
                  onChange={(e) => setInput({ exchangeRate: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label htmlFor={`${uid}-tax`}>Incluir IVA (19%)</Label>
                <Switch
                  id={`${uid}-tax`}
                  checked={input.includeTax}
                  onCheckedChange={(checked) => setInput({ includeTax: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`${uid}-term`}>Plazo de financiamiento (meses)</Label>
                <Input
                  id={`${uid}-term`}
                  type="number"
                  step="1"
                  min={TERM_INPUT_MIN}
                  max={TERM_INPUT_MAX}
                  inputMode="numeric"
                  value={input.termMonths}
                  onChange={(e) => {
                    const parsed = Number.parseInt(e.target.value, 10);
                    if (Number.isNaN(parsed)) return;
                    setInput({ termMonths: parsed });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Cuota mensual informativa. La tasa se ajusta automáticamente al plazo ({RATE_TERM_MIN_MONTHS}m
                  → 3.0%, {RATE_TERM_MAX_MONTHS}m → 5.5%, lineal entre medio).
                </p>
              </div>
            </div>

            {/* ── Column 2: Outputs ──────────────────────────────── */}
            <div className="space-y-2 rounded-md border bg-muted/30 p-4">
              <OutputRow label="Subtotal" value={fmt(displayOutput.subtotal)} />
              <OutputRow label="Descuento" value={`- ${fmt(displayOutput.discountAmount)}`} />
              <OutputRow label="Base imponible" value={fmt(displayOutput.taxableBase)} />
              <OutputRow label="IVA (19%)" value={fmt(displayOutput.taxAmount)} />
              <div className="border-t pt-2">
                <OutputRow label="Total" value={fmt(displayOutput.total)} strong />
              </div>
              <OutputRow
                label="Equivalente (moneda base)"
                value={displayOutput.totalInBaseCurrency.toFixed(2)}
              />
              {canSeeCosts && displayOutput.margin !== null ? (
                <OutputRow label="Margen" value={`${displayOutput.margin.toFixed(2)} %`} />
              ) : null}
              <div className="border-t pt-2">
                <OutputRow
                  label={`Cuota mensual (${input.termMonths} meses)`}
                  value={
                    displayOutput.installment === null
                      ? '—'
                      : `${fmt(displayOutput.installment)} / mes`
                  }
                  strong
                />
              </div>
            </div>

            {/* ── Column 3: Actions ──────────────────────────────── */}
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={handleSendToQuotation}
                disabled={!selectedProduct || !input.currencyId}
              >
                Enviar a cotización
              </Button>
              <Button type="button" variant="outline" onClick={reset}>
                Reiniciar
              </Button>
              {currenciesQuery.isError ? (
                <p className="text-sm text-destructive" role="alert">
                  No se pudieron cargar las monedas.
                </p>
              ) : null}
              {productDetail.isError ? (
                <p className="text-sm text-destructive" role="alert">
                  No se pudo cargar el producto seleccionado.
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuotationForm
        open={quotationOpen}
        onOpenChange={setQuotationOpen}
        {...(quotationInitial ? { initialValues: quotationInitial } : {})}
      />
    </>
  );
}

function OutputRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): ReactElement {
  return (
    <div className={`flex items-center justify-between text-sm ${strong ? 'font-semibold' : ''}`}>
      <span className={strong ? '' : 'text-muted-foreground'}>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
